import React, { useEffect, useState } from "react";
import {
  Alert,
  Grid,
  Typography,
  InputAdornment,
  TextField,
  Button,
  Paper,
  Box,
  Icon
} from "@mui/material";
import ApiList from "../../partials/ApiList";
import ResultField from "../../partials/ResultField";
import { numbersOnly } from "../../../utilities/strings";
import validate from "./validate";
import calculate from "./calculate";
import { DISCLAIMER, SIG_FIGS } from "../../../resources/constants";
import InfoTooltip from '../../partials/InfoTooltip';
import { TextFieldRightAligned } from '../../../utilities/theme';
import AutoCompleteTextField from "../../partials/AutoCompleteTextField";
import ingredients, { TYPES } from '../../../resources/ingredients';

//calulates the bases to show
const bases = ingredients
  .filter((x) => x.type === TYPES.treat)
  .map((x) => ({ ...x, label: x.name, value: x.name }));




/**
 * TreatTab
 * @param {*} props
 * @returns
 */
export default function TreatTab() {
  const [base, setBase] = useState(null);
  const [baseCount, setBaseCount] = useState(1);
  const [mold, setMold] = useState("");
  const [flavorPct, setFlavorPct] = useState("");
  const [treatPct, setTreatPct] = useState("");

  const [apiList, setApiList] = useState([{}]);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});

  //useEffect: reset results if any variables change
  useEffect((_) => {
      setResults(null);
    }, [base, baseCount, mold, flavorPct, treatPct, apiList]
  );

  const getState = (_) => ({ base, baseCount, mold, flavorPct, treatPct, apiList });

  //on button click
  const onCalculate = (_) => {
    const state = getState();
    if (validate(setErrors, state)) {
      calculate(setResults, state);
    }
  };

  //renders
  const renderTitle = (
    <Grid container spacing={1}>
      <Grid item xs={8} py={2}>
        <Typography variant="h4">Veterinary Treats</Typography>
        <Typography variant="body1">
          Please supply the information below...
        </Typography>
      </Grid>
    </Grid>
  );

  const renderDisclaimer = (
    <Grid container spacing={1}>
      <Grid item xs={12} my={2}>
        <Alert severity="warning">{DISCLAIMER}</Alert>
      </Grid>
    </Grid>
  );

  const renderInputs = (
    <Grid container spacing={1}>
      <Grid item md={4}>
        <AutoCompleteTextField
          required
          my={1}
          name="base"
          label="Select a Base *"
          value={base}
          data={bases}
          onChange={(_, v) => setBase(v)}
          error={errors.base}
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          my={1}
          required
          label="Number of Treats"
          value={baseCount}
          onChange={(e) => setBaseCount(numbersOnly(e.target.value))}
          error={errors.baseCount}
          helperText={errors.baseCount}
          fullWidth
          {...TextFieldRightAligned}
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          required
          my={1}
          label="Mold Size"
          value={mold}
          onChange={(e) => setMold(numbersOnly(e.target.value, true))}
          error={errors.mold}
          helperText={errors.mold}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography>mL</Typography>
              </InputAdornment>
            ),
          }}
          {...TextFieldRightAligned}
        />
      </Grid>
      <Grid item md={4}>
        <InfoTooltip title={(<div><Icon className="fa fa-info-circle" />&nbsp;For no flavor, enter "0"</div>)} arrow>
          <TextField
            required
            my={1}
            label="Flavor Desired"
            value={flavorPct}
            onChange={(e) => setFlavorPct(numbersOnly(e.target.value, true))}
            error={errors.flavorPct}
            helperText={errors.flavorPct}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>%</Typography>
                </InputAdornment>
              ),
            }}
            {...TextFieldRightAligned}
          />
        </InfoTooltip>
      </Grid>
      <Grid item md={4}>
        <InfoTooltip title={(<div><Icon className="fa fa-info-circle" />&nbsp;Recommended value of 50-60%</div>)} arrow>
          <TextField
            required
            my={1}
            label="Treat Added"
            value={treatPct}
            onChange={(e) => setTreatPct(numbersOnly(e.target.value, true))}
            error={errors.treatPct}
            helperText={errors.treatPct}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>%</Typography>
                </InputAdornment>
              ),
            }}
            {...TextFieldRightAligned}
          />
        </InfoTooltip>
      </Grid>
    </Grid>
  );

  const renderApiList = (
    <Grid container spacing={1} py={1}>
      <Grid item xs>
        <ApiList
          data={apiList}
          errors={errors.apiList}
          results={results?.apiList}
          onChange={setApiList}
        />
      </Grid>
    </Grid>
  );

  const renderButton = (
    <Grid container spacing={1} py={1}>
      <Grid item xs>
        <Button
          variant="contained"
          color="primary"
          endIcon={<Icon className="fa fa-paper-plane" />}
          onClick={onCalculate}
        >
          Calculate
        </Button>
      </Grid>
    </Grid>
  );

  const renderResults = results && (
    <Grid container spacing={1}>
      <Grid item xs my={2}>
        <Paper variant="secondary">
          <Box p={2}>
            <Typography variant="h5">Results</Typography>
            <ResultField label="Silicon Dioxide Colloidal Quantity">
              {results.siO2.toFixed(SIG_FIGS)} g
            </ResultField>
            {flavorPct > 0 && <ResultField label="Flavor Quantity">
              {results.flavorAmt.toFixed(SIG_FIGS)} mL
            </ResultField>
            }
            {treatPct > 0 && <ResultField label="Treat Quantity">
              {results.treatAmt.toFixed(SIG_FIGS)} g
            </ResultField>
            }
            {false && <ResultField label="Blank Vehicle Quantity">
              {results.vehicleBlank.toFixed(SIG_FIGS)} g
            </ResultField>}
            <ResultField label="Vehicle Quantity">
              {results.vehicle.toFixed(SIG_FIGS)} g
            </ResultField>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Grid container spacing={1}>
      {renderTitle}
      {renderInputs}
      {renderApiList}
      {renderButton}
      {renderResults}
      {renderDisclaimer}
    </Grid>
  );
}
